import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { COMMISSION_CONFIG as C } from "../../data/commissionConfig";
import { computeEstimate, money } from "../../lib/commissionEstimate";
import ReferenceUpload from "./ReferenceUpload";
import EstimateDisplay from "./EstimateDisplay";
import FamilyArchive from "../FamilyArchive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Size", "Vision", "Details", "Estimate", "Request"];

const INITIAL = {
  sizeId: "",
  description: "",
  tags: [],
  images: [],
  figuresId: "",
  complexity: [],
  timelineId: "",
  requestedDate: "",
  name: "",
  email: "",
  phone: "",
  location: "",
  referral: "",
};

export default function CommissionEstimator() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggleIn = (k, v) => () =>
    setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const estimate = useMemo(() => computeEstimate(f), [f]);

  const stepValid = [
    !!f.sizeId,
    f.description.trim().length >= 10,
    !!f.figuresId && !!f.timelineId && (f.timelineId !== "date" || !!f.requestedDate),
    true,
    !!f.name.trim() && /.+@.+\..+/.test(f.email),
  ][step];

  const go = (n) => {
    setStep(n);
    document.getElementById("estimator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!stepValid) return;
    setBusy(true);
    const size = C.sizes.find((s) => s.id === f.sizeId);
    const figures = C.figures.find((x) => x.id === f.figuresId);
    const timeline = C.timelines.find((t) => t.id === f.timelineId);
    try {
      await axios.post(`${API}/commission-estimate`, {
        name: f.name,
        email: f.email,
        phone: f.phone || null,
        location: f.location || null,
        referral: f.referral || null,
        size_id: size.id,
        size_label: size.label,
        base_price: size.price,
        figures: figures.label,
        complexity: f.complexity,
        description: f.description,
        tags: f.tags,
        reference_images: f.images.map(({ id, url, filename }) => ({ id, url, filename })),
        timeline: timeline.label,
        requested_date: f.timelineId === "date" ? f.requestedDate : null,
        estimate_low: estimate.low,
        estimate_high: estimate.high,
        custom_quote: estimate.customQuote,
        custom_quote_reasons: estimate.reasons,
      });
      setDone(true);
      document.getElementById("estimator")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      toast.error("Something went wrong. Please email hello@josephgravesjr.com.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section id="estimator" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24" data-testid="commission-confirmation">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-6 text-[hsl(var(--accent))]">
            <Check className="h-4 w-4" />
            <span className="label !text-[hsl(var(--accent))]">Request received</span>
          </div>
          <h2 className="serif text-4xl md:text-6xl tracking-tight font-light leading-[1.05]">
            Thank you for your commission inquiry.
          </h2>
          <p className="mt-6 text-lg text-[hsl(var(--ink-soft))] leading-relaxed">
            Joseph will review your concept and reference material before
            confirming availability, final pricing, and next steps.
          </p>
          <div className="mt-10 border-t border-border pt-8">
            <EstimateDisplay estimate={estimate} compact />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="estimator" className="border-t border-border scroll-mt-24" data-testid="commission-estimator">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-32 md:pb-28">
        <Progress step={step} onJump={(i) => i < step && go(i)} />

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <form onSubmit={submit} className="lg:col-span-8" noValidate>
            {step === 0 && <StepSize f={f} set={set} />}
            {step === 1 && (
              <StepVision
                f={f}
                set={set}
                toggleIn={toggleIn}
                setImages={(fn) =>
                  setF((s) => ({ ...s, images: typeof fn === "function" ? fn(s.images) : fn }))
                }
              />
            )}
            {step === 2 && <StepDetails f={f} set={set} toggleIn={toggleIn} />}
            {step === 3 && <StepEstimate f={f} estimate={estimate} />}
            {step === 4 && <StepRequest f={f} set={set} />}

            <div className="hidden md:flex items-center justify-between mt-14 pt-8 border-t border-border">
              <NavButtons step={step} stepValid={stepValid} busy={busy} go={go} />
            </div>
          </form>

          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-32">
              <EstimateDisplay estimate={estimate} compact />
              <Summary f={f} estimate={estimate} />
            </div>
          </aside>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border px-6 py-4" data-testid="estimator-mobile-bar">
        <div className="flex items-center justify-between gap-4">
          <MobileEstimate estimate={estimate} />
          <div className="flex items-center gap-2 shrink-0">
            <NavButtons step={step} stepValid={stepValid} busy={busy} go={go} mobile onSubmit={submit} />
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileEstimate({ estimate }) {
  if (!estimate || estimate.incomplete)
    return <p className="text-xs text-[hsl(var(--ink-soft))]">Select a size to see an estimate</p>;
  if (estimate.customQuote)
    return <p className="label !text-[hsl(var(--accent))]" data-testid="mobile-estimate">Custom quote</p>;
  return (
    <div data-testid="mobile-estimate">
      <p className="text-[0.625rem] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">Estimated</p>
      <p className="serif text-base leading-tight whitespace-nowrap">{money(estimate.low)} – {money(estimate.high)}</p>
    </div>
  );
}

function NavButtons({ step, stepValid, busy, go, mobile = false, onSubmit }) {
  const last = step === STEPS.length - 1;
  const size = mobile ? "px-5 py-3" : "px-8 py-4";
  return (
    <>
      {step > 0 ? (
        <button
          type="button"
          onClick={() => go(step - 1)}
          className={`inline-flex items-center gap-2 label link-underline ${mobile ? "" : ""}`}
          data-testid={mobile ? "estimator-back-mobile" : "estimator-back"}
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {mobile ? "" : "Back"}
        </button>
      ) : (
        <span />
      )}
      {last ? (
        <button
          type={mobile ? "button" : "submit"}
          onClick={mobile ? onSubmit : undefined}
          disabled={!stepValid || busy}
          className={`inline-flex items-center gap-3 bg-foreground text-background ${size} text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40 transition-opacity`}
          data-testid={mobile ? "estimator-submit-mobile" : "estimator-submit"}
        >
          {busy ? "Sending…" : mobile ? "Submit" : "Submit Commission Request"}
          {!busy && <ArrowRight className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => go(step + 1)}
          disabled={!stepValid}
          className={`inline-flex items-center gap-3 bg-foreground text-background ${size} text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40 transition-opacity`}
          data-testid={mobile ? "estimator-next-mobile" : "estimator-next"}
        >
          {step === 2 ? "See estimate" : step === 3 ? "Continue to request" : "Continue"}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </>
  );
}

function Progress({ step, onJump }) {
  return (
    <ol className="grid grid-cols-5 gap-2 md:gap-6" data-testid="estimator-progress">
      {STEPS.map((s, i) => {
        const state = i < step ? "done" : i === step ? "active" : "todo";
        return (
          <li key={s} className="min-w-0">
            <button
              type="button"
              onClick={() => onJump(i)}
              disabled={i >= step}
              className={`w-full text-left border-t-2 pt-3 transition-colors ${
                state === "active"
                  ? "border-foreground"
                  : state === "done"
                  ? "border-[hsl(var(--accent))] hover:border-foreground"
                  : "border-border"
              }`}
              data-testid={`estimator-step-${i + 1}`}
              aria-current={state === "active" ? "step" : undefined}
            >
              <span className={`block text-[0.625rem] md:text-xs tracking-[0.2em] ${state === "todo" ? "text-[hsl(var(--ink-soft))]/60" : "text-[hsl(var(--ink-soft))]"}`}>
                0{i + 1}
              </span>
              <span className={`block mt-0.5 text-[0.55rem] md:text-xs uppercase tracking-[0.12em] md:tracking-[0.18em] ${state === "active" ? "text-foreground" : state === "done" ? "text-foreground/80" : "text-[hsl(var(--ink-soft))]/60"}`}>
                {s}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function StepHead({ eyebrow, title, body }) {
  return (
    <header className="mb-10 md:mb-12">
      <p className="label mb-3">{eyebrow}</p>
      <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05]">{title}</h2>
      {body && <p className="mt-5 max-w-xl text-[hsl(var(--ink-soft))] leading-relaxed">{body}</p>}
    </header>
  );
}

function StepSize({ f, set }) {
  return (
    <div data-testid="step-size">
      <StepHead
        eyebrow="01 · Select canvas size"
        title="Where will it live?"
        body="Choose the canvas closest to the wall you have in mind. Each size shows a starting commission investment — the base from which every estimate begins."
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" role="radiogroup">
        {C.sizes.map((s) => {
          const active = f.sizeId === s.id;
          const [w, h] = s.id.split("x").map(Number);
          const ratio = w && h ? w / h : 1;
          return (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => set("sizeId")(s.id)}
              className={`text-left p-4 md:p-5 min-h-[140px] flex flex-col justify-between border transition-colors ${
                active
                  ? "bg-foreground text-background border-foreground"
                  : "border-border hover:border-foreground"
              }`}
              data-testid={`size-option-${s.id}`}
            >
              <span className="block h-10 flex items-start">
                <span
                  className={`block border ${active ? "border-background/70" : "border-[hsl(var(--ink-soft))]/60"}`}
                  style={{ width: ratio >= 1 ? 40 : 40 * ratio, height: ratio >= 1 ? 40 / ratio : 40 }}
                />
              </span>
              <span>
                <span className="serif text-xl md:text-2xl block leading-none">{s.label}</span>
                <span className={`block mt-2 text-[0.625rem] uppercase tracking-[0.16em] ${active ? "text-background/70" : "text-[hsl(var(--ink-soft))]"}`}>
                  {s.price ? `from ${money(s.price)}` : "Custom quote"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-6 text-xs text-[hsl(var(--ink-soft))]">
        Starting Commission Investment · unframed, acrylic on gallery-wrapped canvas.
      </p>
    </div>
  );
}

function StepVision({ f, set, toggleIn, setImages }) {
  return (
    <div className="space-y-14 md:space-y-16" data-testid="step-vision">
      <div>
        <StepHead
          eyebrow="02 · Describe your vision"
          title="Tell me what you're envisioning."
          body="Give me a few words or a short description of your idea. Think subject, mood, colors, people, places, story, or anything that makes the piece personal."
        />
        <textarea
          value={f.description}
          onChange={(e) => set("description")(e.target.value.slice(0, C.descriptionMaxChars))}
          rows={7}
          placeholder="Black cowboy, grandfather, Kansas, sunflowers, warm earth tones, family legacy..."
          className="w-full bg-transparent border border-border p-5 text-base leading-relaxed outline-none focus:border-foreground transition-colors resize-none placeholder:text-[hsl(var(--ink-soft))]/50"
          data-testid="vision-description"
        />
        <p className="mt-2 text-xs text-[hsl(var(--ink-soft))] text-right">
          {f.description.length} / {C.descriptionMaxChars}
        </p>
        <div className="mt-8">
          <FamilyArchive compact />
        </div>
      </div>

      <div>
        <p className="label mb-4">Inspiration tags · optional</p>
        <div className="flex flex-wrap gap-2" data-testid="vision-tags">
          {C.tags.map((t) => {
            const active = f.tags.includes(t);
            return (
              <button
                key={t}
                type="button"
                aria-pressed={active}
                onClick={toggleIn("tags", t)}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.14em] border transition-colors ${
                  active ? "bg-foreground text-background border-foreground" : "border-border text-[hsl(var(--ink-soft))] hover:border-foreground hover:text-foreground"
                }`}
                data-testid={`tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="label mb-3">Upload inspiration / reference images · optional</p>
        <p className="text-[hsl(var(--ink-soft))] leading-relaxed max-w-xl mb-6">
          Photos, colors, locations, clothing, family images, or other visual
          references can help me understand your vision.
        </p>
        <ReferenceUpload images={f.images} setImages={setImages} />
      </div>
    </div>
  );
}

function OptionList({ options, value, onChange, testPrefix, multi = false }) {
  return (
    <div className="border-t border-border" role={multi ? "group" : "radiogroup"}>
      {options.map((o) => {
        const id = o.id ?? o;
        const label = o.label ?? o;
        const active = multi ? value.includes(id) : value === id;
        return (
          <button
            key={id}
            type="button"
            role={multi ? "checkbox" : "radio"}
            aria-checked={active}
            onClick={() => onChange(id)}
            className="w-full flex items-center justify-between gap-4 py-4 md:py-5 border-b border-border text-left group"
            data-testid={`${testPrefix}-${String(id).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
          >
            <span className="flex items-baseline gap-4">
              <span className={`serif text-xl md:text-2xl ${active ? "" : "text-foreground/85 group-hover:text-foreground"}`}>{label}</span>
              {o.note && <span className="label text-[hsl(var(--ink-soft))] hidden sm:inline">{o.note}</span>}
            </span>
            <span
              className={`h-5 w-5 shrink-0 border flex items-center justify-center transition-colors ${
                active ? "bg-foreground border-foreground text-background" : "border-[hsl(var(--ink-soft))]/50"
              } ${multi ? "" : "rounded-full"}`}
            >
              {active && <Check className="h-3 w-3" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StepDetails({ f, set, toggleIn }) {
  const setComplexity = (id) => {
    if (id === "None") return set("complexity")([]);
    toggleIn("complexity", id)();
  };
  return (
    <div className="space-y-14 md:space-y-16" data-testid="step-details">
      <div>
        <StepHead eyebrow="03 · Details" title="How many primary figures or portraits?" />
        <OptionList options={C.figures} value={f.figuresId} onChange={set("figuresId")} testPrefix="figures" />
        <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]">
          Complex compositions or multiple portraits may require additional studio time.
        </p>
      </div>

      <div>
        <p className="label mb-2">Additional subjects</p>
        <h3 className="serif text-2xl md:text-3xl tracking-tight font-light mb-6">Does your concept include any of these?</h3>
        <OptionList
          options={[...C.complexity.options, { id: "None", label: "None" }].map((o) =>
            typeof o === "string" ? { id: o, label: o } : o
          )}
          value={f.complexity.length ? f.complexity : []}
          onChange={setComplexity}
          testPrefix="complexity"
          multi
        />
      </div>

      <div>
        <p className="label mb-2">Timeline</p>
        <h3 className="serif text-2xl md:text-3xl tracking-tight font-light mb-6">When would you like your piece?</h3>
        <OptionList options={C.timelines} value={f.timelineId} onChange={set("timelineId")} testPrefix="timeline" />
        {f.timelineId === "date" && (
          <label className="block mt-6 max-w-xs">
            <span className="label mb-2 block">Desired completion date</span>
            <input
              type="date"
              value={f.requestedDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => set("requestedDate")(e.target.value)}
              className="w-full bg-transparent border-b border-border py-2 text-base outline-none focus:border-foreground"
              data-testid="timeline-date-input"
            />
            <span className="block mt-2 text-xs text-[hsl(var(--ink-soft))]">Requested dates are reviewed against the studio calendar and not guaranteed.</span>
          </label>
        )}
        {f.timelineId === "rush" && (
          <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]" data-testid="rush-note">
            Rush availability is subject to studio schedule and must be approved.
          </p>
        )}
      </div>
    </div>
  );
}

function StepEstimate({ f, estimate }) {
  return (
    <div data-testid="step-estimate">
      <p className="label mb-3">04 · Estimate</p>
      <EstimateDisplay estimate={estimate} />
      <div className="mt-12 lg:hidden">
        <Summary f={f} estimate={estimate} />
      </div>
    </div>
  );
}

function Summary({ f, estimate }) {
  const size = C.sizes.find((s) => s.id === f.sizeId);
  const figures = C.figures.find((x) => x.id === f.figuresId);
  const timeline = C.timelines.find((t) => t.id === f.timelineId);
  const rows = [
    ["Canvas", size ? `${size.label}${size.price ? ` · from ${money(size.price)}` : ""}` : null],
    ["Figures", figures?.label],
    ["Subjects", f.complexity.length ? f.complexity.join(", ") : f.figuresId ? "None" : null],
    ["Timeline", timeline ? `${timeline.label}${f.timelineId === "date" && f.requestedDate ? ` · ${f.requestedDate}` : ""}` : null],
    ["Tags", f.tags.length ? f.tags.join(", ") : null],
    ["References", f.images.length ? `${f.images.length} image${f.images.length === 1 ? "" : "s"}` : null],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="mt-8 border-t border-border text-sm" data-testid="estimate-summary">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-6 py-3 border-b border-border">
          <dt className="label w-24 shrink-0 text-[hsl(var(--ink-soft))]">{k}</dt>
          <dd className="text-foreground/90">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="label mb-2 block">{label}</span>
      <input
        {...props}
        className="w-full bg-transparent border-b border-border py-2.5 text-base outline-none focus:border-foreground transition-colors"
        data-testid={testid}
      />
    </label>
  );
}

function StepRequest({ f, set }) {
  return (
    <div data-testid="step-request">
      <StepHead
        eyebrow="05 · Request"
        title="Where should Joseph reply?"
        body="Nothing is charged at this stage. Your request goes directly to the studio for personal review."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Input label="Name" required value={f.name} onChange={(e) => set("name")(e.target.value)} testid="request-name" autoComplete="name" />
        <Input label="Email" required type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} testid="request-email" autoComplete="email" />
        <Input label="Phone · optional" type="tel" value={f.phone} onChange={(e) => set("phone")(e.target.value)} testid="request-phone" autoComplete="tel" />
        <Input label="City / State" value={f.location} onChange={(e) => set("location")(e.target.value)} testid="request-location" placeholder="Denver, CO" />
        <label className="block md:col-span-2">
          <span className="label mb-2 block">How did you hear about Joseph Graves Jr.?</span>
          <select
            value={f.referral}
            onChange={(e) => set("referral")(e.target.value)}
            className="w-full bg-transparent border-b border-border py-2.5 text-base outline-none focus:border-foreground"
            data-testid="request-referral"
          >
            <option value="">Select…</option>
            {C.referralSources.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-8 text-xs text-[hsl(var(--ink-soft))] max-w-md">
        Your details are kept private and used only to scope the commission.
      </p>
    </div>
  );
}
